<script>
    import { Dropdown } from 'bootstrap';
    import utilsService from '@/services/core/utils.service';

    export default {
        props: {
            buttonText: String,
            // Accessible name for the toggle. Needed whenever the button slot holds only an icon:
            // without it the control has no accessible name. Optional, so callers that render text
            // are unaffected.
            ariaLabel: String,
            buttonClass: String,
            buttonStyle: String,
            menuClass: String,
            menuStyle: String,
            itemClass: String,
            itemStyle: String,
            placement: {
                /** placement opitions -> https://popper.js.org/docs/v2/constructors/#options */
                type: String,
                default: 'bottom-end'
            },
            strategy: {
                /** strategy opitions -> https://popper.js.org/docs/v2/constructors/#strategy */
                type: String,
                default: 'fixed'
            },
            options: Array,
            optionTextKey: {
                type: String,
                default: 'text'
            },
            noCaret: Boolean,
            offset: [String, Number],
            autoclose: {
                /** [true (always in next click), false (never), 'outside', 'inside'] */
                type: [Boolean, String],
                default: true
            },
            active: Function,
            disabled: Boolean,
            noBtnGroup: Boolean
        },
        emits: ['buttonClick', 'itemClick', 'show', 'hide'],
        data() {
            return {
                id: `dropdown-${utilsService.uuid()}`,
                isVisible: false,
                dropdown: null
            };
        },
        computed: {
            dropdownConfig() {
                const config = {
                    autoClose: this.autoclose,
                    offset: utilsService.isNumber(this.offset) ? [+this.offset, 2] : [0, 2],
                    popperConfig: {
                        placement: this.placement,
                        strategy: this.strategy
                    }
                };

                return JSON.stringify(config);
            }
        },
        methods: {
            onBtnToogleClick(e) {
                this.$emit('buttonClick', e);
            },
            show() {
                if (typeof this.dropdown?.show === 'function') this.dropdown?.show();
            },
            hide() {
                if (typeof this.dropdown?.hide === 'function') this.dropdown?.hide();
            },
            onDropdownItemClick(op, e) {
                typeof op.click === 'function' && op.click(op);
                this.$emit('itemClick', op, e);
            },
            isActive(op) {
                return typeof this.active === 'function' ? this.active(op) : this.active;
            },
            _onDropdownShow() {
                this.$emit('show');
                this.isVisible = true;
            },
            _onDropdownHide() {
                this.$emit('hide');
                this.isVisible = false;
            }
        },
        mounted() {
            const btnToogle = this.$refs.btnToogle;

            if (btnToogle) {
                this.dropdown = new Dropdown(btnToogle);

                btnToogle.addEventListener('show.bs.dropdown', this._onDropdownShow);
                btnToogle.addEventListener('hide.bs.dropdown', this._onDropdownHide);
            }
        },
        beforeUnmount() {
            if (this.isVisible) this.hide();
        }
    };
</script>

<template>
    <span class="gsb-dropdown dropdown" :class="{ 'btn-group': !noBtnGroup }">
        <button
            ref="btnToogle"
            type="button"
            class="gsb-dropdown-toggle btn shadow-none"
            data-bs-toggle="dropdown"
            :data-bs-config="dropdownConfig"
            :class="[{ 'dropdown-toggle': !noCaret }, buttonClass]"
            :id="id"
            :style="buttonStyle"
            :disabled="disabled"
            :aria-label="ariaLabel"
            @click="e => onBtnToogleClick(e)"
        >
            <template v-if="buttonText">{{ buttonText }}</template>
            <slot v-else name="button" />
        </button>
        <div class="dropdown-menu" :aria-labelledby="id" :class="menuClass" :style="menuStyle">
            <template v-if="isVisible">
                <slot name="header" />

                <template v-if="options && options?.length > 0">
                    <li v-for="(op, i) in options" :key="`${id}-${i}`" @click="e => onDropdownItemClick(op, e)">
                        <a
                            v-if="!$slots.item"
                            href="javascript:;"
                            class="dropdown-item"
                            :class="[itemClass, { active: isActive(op) }]"
                            :style="itemStyle"
                            >{{ (op || {})[optionTextKey] || op }}</a
                        >

                        <slot v-else name="item" :data="{ item: op, index: i }" />
                    </li>
                </template>

                <slot />
            </template>
        </div>
    </span>
</template>

<style lang="scss">
    $hover-color: #eaeaea;

    .gsb-dropdown {
        .gsb-dropdown-toggle {
            padding: 0.4rem 1rem;
        }

        .dropdown-menu {
            max-height: 30rem;
            overflow-y: auto;

            .dropdown-item {
                cursor: pointer;

                &:not(.active):hover {
                    background: $hover-color linear-gradient(180deg, #efefef, $hover-color) repeat-x;
                }

                &:active {
                    color: inherit;
                    text-decoration: none;
                    background-color: $hover-color;
                }
            }
        }
    }
</style>
